import { useActivePlayer } from '@/context/ActivePlayerContext';
import { Fragment, useEffect, useState } from 'react';
import { PlayerForSaveDto, TourBagDetail, TourBagIronDetail, TourBagIronSet, TourBagMstrPayload } from '@/api/models';
import { useBackgroundSync } from '@/context/BackgroundSyncContext';
import transferStyles from './TransferClub.module.scss';
import clubStyles from '../Club.module.scss';
import { PutterReadOnly } from '../Putter';
import Checkbox from '@/common/Checkbox';
import SearchBar from './SearchBar';
import { TransferClubPayloadDetail } from '@/api/models/TransferClubsPayload';
import { postTransferClubs } from '@/api/endpoints';
import { TourBagService } from '@/service';
import SpinnerOverlay from '@/common/SpinnerOverlay';
import { MetalReadOnly } from '../Metals';
import { WedgeReadOnly } from '../Wedge/Wedge';
import { IronReadOnly, IronSetReadOnly } from '../Iron';
import { IronReadOnly as UtilityReadOnly, IronSetReadOnly as UtilitySetReadOnly } from '../Utility';
import { headTypeMap } from '@/constants';
import { isMobileResolution } from '@/utils';
import { useToast } from '@/context/ToastContext';
import useOnlineStatus from '@/hooks/useOnlineStatus';

interface PopupList {
  id: number;
  description: string;
  serial: string;
  isActive: boolean;
}

function TransferClub() {
  const { tourBag, player, permissions, updateLocalTourBag } = useActivePlayer();
  const tourPageSettings = permissions?.tourPageSettings;
  const { isSyncComplete } = useBackgroundSync();
  const isOnline = useOnlineStatus();
  const [currentTourBag, setCurrentTourBag] = useState(tourBag);
  const [transfereePlayer, setTransfereePlayer] = useState(null as PlayerForSaveDto | null);
  const [driverClubs, setDriverClubs] = useState([] as TourBagDetail[]);
  const [fairwayClubs, setFairwayClubs] = useState([] as TourBagDetail[]);
  const [hybridClubs, setHybridClubs] = useState([] as TourBagDetail[]);
  const [wedgeClubs, setWedgeClubs] = useState([] as TourBagDetail[]);
  const [putterClubs, setPutterClubs] = useState([] as TourBagDetail[]);
  const [utilityClubs, setUtilityClubs] = useState([] as TourBagIronDetail[]);
  const [utilitySets, setUtilitySets] = useState([] as TourBagIronSet[]);
  const [ironClubs, setIronClubs] = useState([] as TourBagIronDetail[]);
  const [ironSets, setIronSets] = useState([] as TourBagIronSet[]);
  const [modalClubList, setModalClubList] = useState(new Map<string, PopupList[]>());
  const [clubCount, setClubCount] = useState(0);
  const [clubsInBag, setClubsInBag] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);
  const [accordionSelectedId, setAccordionSelectedId] = useState(0);
  const isTransferClubEnabled = Object.values(tourPageSettings || {}).every((page) => page !== 'readonly');
  const { showToast } = useToast();

  useEffect(() => {
    updateTourBagAndClubs(currentTourBag || []);
  }, [currentTourBag]);

  const updateTourBagAndClubs = (thisTourBag: TourBagMstrPayload[]) => {
    setCurrentTourBag(thisTourBag);
    //Drivers
    let thisDriverBag = thisTourBag?.find((bag) => bag.headTypeDescription.toLowerCase() === 'driver');
    if (thisDriverBag) {
      thisDriverBag.tourBagDetail.sort((a, b) => new Date(b.modifyDate).getTime() - new Date(a.modifyDate).getTime()).sort((a, b) => Number(b.isActive) - Number(a.isActive));
      setDriverClubs(thisDriverBag.tourBagDetail);
    }
    //Fairways
    let thisFairwayBag = thisTourBag?.find((bag) => bag.headTypeDescription.toLowerCase() === 'fairway');
    if (thisFairwayBag) {
      thisFairwayBag.tourBagDetail.sort((a, b) => new Date(b.modifyDate).getTime() - new Date(a.modifyDate).getTime()).sort((a, b) => Number(b.isActive) - Number(a.isActive));
      setFairwayClubs(thisFairwayBag.tourBagDetail);
    }
    //Hybrids
    let thisHybridBag = thisTourBag?.find((bag) => bag.headTypeDescription.toLowerCase() === 'hybrid');
    if (thisHybridBag) {
      thisHybridBag.tourBagDetail.sort((a, b) => new Date(b.modifyDate).getTime() - new Date(a.modifyDate).getTime()).sort((a, b) => Number(b.isActive) - Number(a.isActive));
      setHybridClubs(thisHybridBag.tourBagDetail);
    }
    //Wedges
    let thisWedgeBag = thisTourBag?.find((bag) => bag.headTypeDescription.toLowerCase() === 'wedge');
    if (thisWedgeBag) {
      thisWedgeBag.tourBagDetail.sort((a, b) => new Date(b.modifyDate).getTime() - new Date(a.modifyDate).getTime()).sort((a, b) => Number(b.isActive) - Number(a.isActive));
      setWedgeClubs(thisWedgeBag.tourBagDetail);
    }
    const putterBag = thisTourBag?.find((bag) => bag.headTypeDescription === 'Putter');
    setPutterClubs(putterBag?.tourBagDetail || []);
    const utilityBag = thisTourBag?.find((bag) => bag.headTypeDescription === 'Utility');
    setUtilityClubs(utilityBag?.tourBagIronDetail || []);
    setUtilitySets(utilityBag?.tourBagIronSets || []);
    const ironBag = thisTourBag?.find((bag) => bag.headTypeDescription === 'Iron');
    setIronClubs(ironBag?.tourBagIronDetail || []);
    setIronSets(ironBag?.tourBagIronSets || []);
    //create empty list to track the clubs that were checked (and effectively resetting the list after a save)
    var modalHeadTypes = new Map<string, PopupList[]>();
    modalHeadTypes.set('Driver', [] as PopupList[]);
    modalHeadTypes.set('Fairway', [] as PopupList[]);
    modalHeadTypes.set('Hybrid', [] as PopupList[]);
    modalHeadTypes.set('Wedge', [] as PopupList[]);
    modalHeadTypes.set('Putter', [] as PopupList[]);
    modalHeadTypes.set('Utility', [] as PopupList[]);
    modalHeadTypes.set('Utility Set', [] as PopupList[]);
    modalHeadTypes.set('Iron', [] as PopupList[]);
    modalHeadTypes.set('Iron Set', [] as PopupList[]);
    setModalClubList(modalHeadTypes);
    setClubCount(0);
  };

  const handleTransferClubCheckbox = (id: number, isSelected: boolean, headType: string) => {
    switch (headType) {
      case 'Driver':
        var driver = driverClubs.find((bag) => bag.tourBagDetailId == id);
        driver && updatePopupList(id, driver.headModelName, driver.laserNo, headType, driver.isActive, isSelected);
        break;
      case 'Fairway':
        var fairway = fairwayClubs.find((bag) => bag.tourBagDetailId == id);
        fairway && updatePopupList(id, fairway.headModelName, fairway.laserNo, headType, fairway.isActive, isSelected);
        break;
      case 'Hybrid':
        var hybrid = hybridClubs.find((bag) => bag.tourBagDetailId == id);
        hybrid && updatePopupList(id, hybrid.headModelName, hybrid.laserNo, headType, hybrid.isActive, isSelected);
        break;
      case 'Wedge':
        var wedge = wedgeClubs.find((bag) => bag.tourBagDetailId == id);
        wedge && updatePopupList(id, wedge.headModelName, wedge.laserNo, headType, wedge.isActive, isSelected);
        break;
      case 'Putter':
        var putter = putterClubs.find((bag) => bag.tourBagDetailId == id);
        putter && updatePopupList(id, putter.headModelName, putter.laserNo, headType, putter.isActive, isSelected);
        break;
      case 'Utility':
        var utility = utilityClubs.find((bag) => bag.tourBagIronDetailId == id);
        utility && updatePopupList(id, utility.familyName + ' ' + utility.headModelName, '', headType, utility.isActive, isSelected);
        break;
      case 'Utility Set':
        var utilitySet = utilitySets.find((bag) => bag.tourBagIronSetId == id);
        utilitySet && updatePopupList(id, utilitySet.tourBagIronSetName, '', headType, true, isSelected);
        break;
      case 'Iron':
        var iron = ironClubs.find((bag) => bag.tourBagIronDetailId == id);
        iron && updatePopupList(id, iron.familyName + ' ' + iron.headModelName, '', headType, iron.isActive, isSelected);
        break;
      case 'Iron Set':
        var ironSet = ironSets.find((bag) => bag.tourBagIronSetId == id);
        ironSet && updatePopupList(id, ironSet.tourBagIronSetName, '', headType, true, isSelected);
        break;
    }
  };

  const updatePopupList = (id: number, description: string, laserNo: string, headType: string, isActive: boolean, isSelected: boolean) => {
    let popupItems = modalClubList.get(headType);
    let numberClubs = clubCount;
    if (isSelected) {
      popupItems?.push({ id: id, description: description, serial: laserNo.length > 0 ? '#' + laserNo : '', isActive: isActive });
      numberClubs++;
    } else {
      popupItems = popupItems?.filter((p) => p.id != id);
      numberClubs--;
    }
    setModalClubList((prevMap) => {
      const newMap = new Map(prevMap);
      newMap.set(headType, popupItems || []);
      return newMap;
    });
    setClubCount(numberClubs);
  };

  const handleOpenModel = () => {
    if (clubCount > 0) {
      setShowModal(true);
    }
  };
  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleSelectPlayer = (selectedPlayer: PlayerForSaveDto | null) => {
    setTransfereePlayer(selectedPlayer);
  };

  const handleClubsInBag = () => {
    setClubsInBag(!clubsInBag);
  };

  //send the selected clubs to the API to be transferred
  const performTransfer = async () => {
    if (transfereePlayer == null) return;
    try {
      setShowModal(false);
      setShowSpinner(true);
      //get tourbag for desination player
      const tourBagTransferee = await TourBagService.getTourBagData(transfereePlayer?.playerId || 0);
      let driverPayloadTransferee = [] as TransferClubPayloadDetail[];
      let fairwayPayloadTransferee = [] as TransferClubPayloadDetail[];
      let hybridPayloadTransferee = [] as TransferClubPayloadDetail[];
      let wedgePayloadTransferee = [] as TransferClubPayloadDetail[];
      let putterPayloadTransferee = [] as TransferClubPayloadDetail[];
      let utilityPayloadTransferee = [] as TransferClubPayloadDetail[];
      let utilitySetPayloadTransferee = [] as TransferClubPayloadDetail[];
      let ironPayloadTransferee = [] as TransferClubPayloadDetail[];
      let ironSetPayloadTransferee = [] as TransferClubPayloadDetail[];
      const driverPayload = getClubPayloadTransferor('Driver');
      if (driverPayload.length > 0) {
        driverPayloadTransferee = getClubPayloadTransferee('Driver', tourBagTransferee);
      }
      const fairwayPayload = getClubPayloadTransferor('Fairway');
      if (fairwayPayload.length > 0) {
        fairwayPayloadTransferee = getClubPayloadTransferee('Fairway', tourBagTransferee);
      }
      const hybridPayload = getClubPayloadTransferor('Hybrid');
      if (hybridPayload.length > 0) {
        hybridPayloadTransferee = getClubPayloadTransferee('Hybrid', tourBagTransferee);
      }
      const wedgePayload = getClubPayloadTransferor('Wedge');
      if (wedgePayload.length > 0) {
        wedgePayloadTransferee = getClubPayloadTransferee('Wedge', tourBagTransferee);
      }
      const putterPayload = getClubPayloadTransferor('Putter');
      if (putterPayload.length > 0) {
        putterPayloadTransferee = getClubPayloadTransferee('Putter', tourBagTransferee);
      }
      const utilityPayload = getClubPayloadTransferor('Utility');
      if (utilityPayload.length > 0) {
        ironPayloadTransferee = getClubPayloadTransferee('Utility', tourBagTransferee);
      }
      const utilitySetPayload = getSetPayloadTransferor('Utility Set');
      if (utilitySetPayload.length > 0) {
        utilitySetPayloadTransferee = getSetPayloadTransferee('Utility Set', tourBagTransferee);
      }
      const ironPayload = getClubPayloadTransferor('Iron');
      if (ironPayload.length > 0) {
        ironPayloadTransferee = getClubPayloadTransferee('Iron', tourBagTransferee);
      }
      const ironSetPayload = getSetPayloadTransferor('Iron Set');
      if (ironSetPayload.length > 0) {
        ironSetPayloadTransferee = getSetPayloadTransferee('Iron Set', tourBagTransferee);
      }

      const payload = {
        sourcePlayerId: player?.playerId || 0,
        destinationPlayerId: transfereePlayer?.playerId || 0,
        transfereeTourBagDetail: [...driverPayloadTransferee, ...fairwayPayloadTransferee, ...hybridPayloadTransferee, ...wedgePayloadTransferee, ...putterPayloadTransferee],
        transfereeTourBagIronDetail: [...utilityPayloadTransferee, ...utilitySetPayloadTransferee, ...ironPayloadTransferee, ...ironSetPayloadTransferee],
        transferorTourBagDetail: [...driverPayload, ...fairwayPayload, ...hybridPayload, ...wedgePayload, ...putterPayload],
        transferorTourBagIronDetail: [...utilityPayload, ...utilitySetPayload, ...ironPayload, ...ironSetPayload],
      };

      const response = await postTransferClubs(payload);
      const results = response.data;
      if (!Array.isArray(results) || results.length === 0 || !results.every((result) => result.item2 === 'success')) {
        throw new Error('Transfer failed.');
      }
      const latestBag = await TourBagService.getTourBagData(player?.playerId || 0);
      if (!latestBag) throw new Error('Tour bag data missing.');
      await updateLocalTourBag(latestBag);
      updateTourBagAndClubs(latestBag);
      setShowSpinner(false);
    } catch {
      showToast({ message: 'Failed to Transfer Clubs', status: 'error' });
      setShowSpinner(false);
    }
  };

  //sourcde player
  const getClubPayloadTransferor = (headType: string) => {
    const list = modalClubList.get(headType);
    const payload = list?.map((l) => ({ id: l.id, isActive: clubsInBag }) as TransferClubPayloadDetail) || [];
    return payload;
  };

  //source player
  const getSetPayloadTransferor = (headType: string) => {
    const selectedList = modalClubList.get(headType)?.map((list) => list.id);
    let setPayload = [] as TransferClubPayloadDetail[];
    headType = headType.split(' ')[0];
    const bag = currentTourBag?.find((tb) => tb.headTypeDescription.toLowerCase() == headType.toLowerCase());
    bag?.tourBagIronSets.forEach((set) => {
      if (selectedList?.includes(set.tourBagIronSetId)) {
        setPayload.push(...(set.tourBagIronDetails.map((iron) => ({ id: iron.tourBagIronDetailId, isActive: clubsInBag }) as TransferClubPayloadDetail) || []));
      }
    });
    return setPayload;
  };

  //destination player
  const getClubPayloadTransferee = (headType: string, destinationBag: TourBagMstrPayload[] | undefined) => {
    let payload = [] as TransferClubPayloadDetail[] | undefined;
    //destination clubs
    const destinationClubs = destinationBag?.find((bag) => bag.headTypeDescription == headType.split(' ')[0]); //drop "Set" off the description
    switch (headType) {
      case 'Driver':
      case 'Fairway':
      case 'Hybrid':
      case 'Wedge':
      case 'Putter':
        payload = destinationClubs && destinationClubs.tourBagDetail.map((club) => ({ id: club.tourBagDetailId, isActive: club.isActive }) as TransferClubPayloadDetail);
        break;
      case 'Utility':
      case 'Iron':
        payload = destinationClubs && destinationClubs.tourBagIronDetail.map((club) => ({ id: club.tourBagIronDetailId, isActive: club.isActive }) as TransferClubPayloadDetail);
        break;
    }
    return payload || [];
  };

  //destination player
  const getSetPayloadTransferee = (headType: string, destinationBag: TourBagMstrPayload[] | undefined) => {
    let setPayload = [] as TransferClubPayloadDetail[];
    headType = headType.split(' ')[0];
    let bag = destinationBag?.find((b) => b.headTypeDescription.toLowerCase() == headType.toLowerCase());
    bag?.tourBagIronSets.forEach((set) => {
      setPayload.push(...(set.tourBagIronDetails.map((iron) => ({ id: iron.tourBagIronDetailId, isActive: iron.isActive }) as TransferClubPayloadDetail) || []));
    });
    return setPayload;
  };

  const handleOpenAccordion = (id: number) => {
    //toggle the accordion on/off based on if
    let newId = accordionSelectedId != id ? id : 0;
    setAccordionSelectedId(newId);
  };

  return (
    <>
      <div className={transferStyles.banner}>
        <div className={transferStyles.title}>
          Select Clubs
          {!isMobileResolution && <span> to Transfer</span>}
        </div>
        {isTransferClubEnabled && isSyncComplete && isOnline && (
          <button className={`${transferStyles.transferButton} ${clubCount == 0 ? transferStyles.disabled : transferStyles.enabled}`} onClick={handleOpenModel}>
            Transfer
            {!isMobileResolution && <span> Selected</span>}
          </button>
        )}
      </div>
      <div className={transferStyles.container} data-testid="tourbag-transfer-club-edit-form-root">
        {driverClubs.length > 0 && (
          <>
            <div className={transferStyles.tileRow}>
              <div className={transferStyles.category} data-testid="tourbag-transfer-club-category-group">
                Driver
              </div>
            </div>
            <MetalReadOnly
              metalClubs={driverClubs}
              currentAccordionId={accordionSelectedId}
              showCheckbox={tourPageSettings?.iron !== 'readonly' && isSyncComplete}
              isClubSelected={(id: number) => Boolean(modalClubList.get('Driver')?.some((c) => c.id === id))}
              handleCheckboxEvent={(id, isChecked) => handleTransferClubCheckbox(id, isChecked, 'Driver')}
              handleOpenAccordion={handleOpenAccordion}
              headTypeId={headTypeMap['Driver']}
            />
          </>
        )}
        {fairwayClubs.length > 0 && (
          <>
            <div className={transferStyles.tileRow}>
              <div className={transferStyles.category} data-testid="tourbag-transfer-club-category-group">
                Fairway
              </div>
            </div>
            <MetalReadOnly
              metalClubs={fairwayClubs}
              currentAccordionId={accordionSelectedId}
              showCheckbox={tourPageSettings?.iron !== 'readonly' && isSyncComplete}
              isClubSelected={(id: number) => Boolean(modalClubList.get('Fairway')?.some((c) => c.id === id))}
              handleCheckboxEvent={(id, isChecked) => handleTransferClubCheckbox(id, isChecked, 'Fairway')}
              handleOpenAccordion={handleOpenAccordion}
              headTypeId={headTypeMap['Fairway']}
            />
          </>
        )}
        {hybridClubs.length > 0 && (
          <>
            <div className={transferStyles.tileRow}>
              <div className={transferStyles.category} data-testid="tourbag-transfer-club-category-group">
                Hybrid
              </div>
            </div>
            <MetalReadOnly
              metalClubs={hybridClubs}
              currentAccordionId={accordionSelectedId}
              showCheckbox={tourPageSettings?.iron !== 'readonly' && isSyncComplete}
              isClubSelected={(id: number) => Boolean(modalClubList.get('Hybrid')?.some((c) => c.id === id))}
              handleCheckboxEvent={(id, isChecked) => handleTransferClubCheckbox(id, isChecked, 'Hybrid')}
              handleOpenAccordion={handleOpenAccordion}
              headTypeId={headTypeMap['Hybrid']}
            />
          </>
        )}
        {wedgeClubs.length > 0 && (
          <>
            <div className={transferStyles.tileRow}>
              <div className={transferStyles.category} data-testid="tourbag-transfer-club-category-group">
                Wedge
              </div>
            </div>
            <WedgeReadOnly
              wedgeClubs={wedgeClubs}
              currentAccordionId={accordionSelectedId}
              showCheckbox={tourPageSettings?.iron !== 'readonly' && isSyncComplete}
              isClubSelected={(id: number) => Boolean(modalClubList.get('Wedge')?.some((c) => c.id === id))}
              handleCheckboxEvent={(id, isChecked) => handleTransferClubCheckbox(id, isChecked, 'Wedge')}
              handleOpenAccordion={handleOpenAccordion}
            />
          </>
        )}
        {putterClubs.length > 0 && (
          <>
            <div className={transferStyles.tileRow}>
              <div className={transferStyles.category} data-testid="tourbag-transfer-club-category-group">
                Putter
              </div>
            </div>
            <PutterReadOnly
              putterClubs={putterClubs || []}
              currentAccordionId={accordionSelectedId}
              showCheckbox={tourPageSettings?.iron !== 'readonly' && isSyncComplete}
              isClubSelected={(id: number) => Boolean(modalClubList.get('Putter')?.some((c) => c.id === id))}
              handleCheckboxEvent={(id, isChecked) => handleTransferClubCheckbox(id, isChecked, 'Putter')}
              handleOpenAccordion={handleOpenAccordion}
            />
          </>
        )}
        {utilityClubs.length + utilitySets.length > 0 && (
          <>
            <div className={transferStyles.tileRow}>
              <div className={transferStyles.category} data-testid="tourbag-transfer-club-category-group">
                Utility
              </div>
            </div>
            <UtilityReadOnly
              ironClubs={utilityClubs}
              currentAccordionId={accordionSelectedId}
              showCheckbox={tourPageSettings?.iron !== 'readonly' && isSyncComplete}
              isClubSelected={(id: number) => Boolean(modalClubList.get('Utility')?.some((c) => c.id === id))}
              handleCheckboxEvent={(id, isChecked) => handleTransferClubCheckbox(id, isChecked, 'Utility')}
              handleOpenAccordion={handleOpenAccordion}
            />
            <UtilitySetReadOnly
              ironSets={utilitySets}
              currentAccordionId={accordionSelectedId}
              showCheckbox={tourPageSettings?.iron !== 'readonly' && isSyncComplete}
              isClubSelected={(id: number) => Boolean(modalClubList.get('Utility Set')?.some((c) => c.id === id))}
              handleCheckboxEvent={(id, isChecked) => handleTransferClubCheckbox(id, isChecked, 'Utility Set')}
              handleOpenAccordion={handleOpenAccordion}
            />
          </>
        )}
        {ironClubs.length + ironSets.length > 0 && (
          <>
            <div className={transferStyles.tileRow}>
              <div className={transferStyles.category} data-testid="tourbag-transfer-club-category-group">
                Iron
              </div>
            </div>
            <IronReadOnly
              ironClubs={ironClubs}
              currentAccordionId={accordionSelectedId}
              showCheckbox={tourPageSettings?.iron !== 'readonly' && isSyncComplete}
              isClubSelected={(id: number) => Boolean(modalClubList.get('Iron')?.some((c) => c.id === id))}
              handleCheckboxEvent={(id, isChecked) => handleTransferClubCheckbox(id, isChecked, 'Iron')}
              handleOpenAccordion={handleOpenAccordion}
            />
            <IronSetReadOnly
              ironSets={ironSets}
              currentAccordionId={accordionSelectedId}
              showCheckbox={tourPageSettings?.iron !== 'readonly' && isSyncComplete}
              isClubSelected={(id: number) => Boolean(modalClubList.get('Iron Set')?.some((c) => c.id === id))}
              handleCheckboxEvent={(id, isChecked) => handleTransferClubCheckbox(id, isChecked, 'Iron Set')}
              handleOpenAccordion={handleOpenAccordion}
            />
          </>
        )}
      </div>
      {showModal && (
        <div className={transferStyles.modalBackdrop}>
          <div className={transferStyles.modal}>
            <div className={transferStyles.title}>Transfer Clubs ({clubCount})</div>
            <div className={transferStyles.titleRow}>
              <div className={transferStyles.name}>
                From {player?.firstName} {player?.lastName}:
              </div>
            </div>
            <div className={transferStyles.clubContainer}>
              {Array.from(modalClubList.keys()).map((headType) => (
                <Fragment key={headType}>
                  {modalClubList.get(headType)?.map((club) => {
                    return (
                      <div key={club.id} className={transferStyles.modalRow}>
                        <div className={transferStyles.head}>{headType}</div>
                        <div className={transferStyles.head}>{club.description}</div>
                        <div className={transferStyles.laserNo}>{club.serial}</div>
                      </div>
                    );
                  })}
                </Fragment>
              ))}
            </div>
            <div className={transferStyles.titleRow}>
              <div className={transferStyles.name}>To Recipient:</div>
            </div>
            <div>
              <SearchBar placeholder="Search Players" classNames={transferStyles.searchBar} selectPlayer={handleSelectPlayer} />
            </div>
            <div>
              <Checkbox label="Set clubs to In the Bag" checked={clubsInBag} onClick={handleClubsInBag} />
            </div>
            <div className={transferStyles.buttonRow}>
              <button className={`${transferStyles.cancelButton}`} onClick={handleCloseModal}>
                Cancel
              </button>
              <button className={`${clubStyles.saveButton} ${transfereePlayer == null ? transferStyles.disabled : ''}`} onClick={performTransfer}>
                Transfer
              </button>
            </div>
          </div>
        </div>
      )}
      {showSpinner && <SpinnerOverlay />}
    </>
  );
}

export default TransferClub;
